import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle, Search } from "lucide-react";
import SmallLoadingSpinner from "../assets/SmallLoadingSpinner";
import ItemSearchModal from "../components/ItemSearchModal";
import { useItemCache } from "../context/ItemCacheContext";

interface SpreadsheetItemInputProps {
  value?: string;
  displayValue?: string; // Direct display value - skips cache lookup for fast rendering
  onChange: (value: string) => void;
  displayBy: 'code' | 'name'; // Display item code or name
  enableValidation?: boolean; // Enable code validation on Enter (exact/partial match)
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSelect?: () => void; // Called after item is selected - use to move focus to next field
  onNotFound?: (code: string) => void; // Zero matches on Enter: notify caller and KEEP the typed text (default: clear it)
  onDoubleClick?: React.MouseEventHandler<HTMLInputElement>;
  enableRef?: boolean;
  disabled?: boolean;
  size?: keyof typeof SIZE_STYLES;
  /** Comma-separated accounting-type group keys — preferred over the code prefix (rule: group, not first digit). */
  accountingTypeGroup?: string;
  accountingTypeCodePrefix?: string; // Restricts offered items to this accounting-type code prefix (e.g. "3" = finished products, "4" = semi-finished/materials). Accepts a comma-separated list ("3,402") to combine a group with individual codes.
  hasIngredient?: boolean; // Restricts the offered items to those that have a normativ (Основна Спецификација)
  statusMode?: 'notInactive'; // Allows any status except one containing "inactive" (default: active only)
}

const SIZE_STYLES = {
  table: {
    wrapper: "w-full h-6",
    inner: "relative h-6",
    input: "w-full h-6 pl-5 pr-5 bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 disabled:bg-gray-100 disabled:cursor-not-allowed",
    iconSize: 14,
    searchButtonLeft: "left-1",
    searchButtonExtra: "",
  },
  // Same as `table`, but a disabled cell (a disabled <fieldset> on an archived / signed
  // normativ, or `disabled`) reads as plain text on the white cell — no grey box, no
  // search icon. Editing looks exactly like `table`.
  tablePlain: {
    wrapper: "w-full h-6",
    inner: "relative h-6",
    input: "w-full h-6 pl-5 pr-5 bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 disabled:bg-transparent disabled:text-gray-800 disabled:opacity-100 disabled:cursor-default",
    iconSize: 14,
    searchButtonLeft: "left-1",
    searchButtonExtra: "disabled:hidden",
  },
  // RN plan columns (copied from the normativ, never edited there): read-only text, no search icon.
  tablePlan: {
    wrapper: "w-full h-6",
    inner: "relative h-6",
    input: "w-full h-6 pl-5 pr-5 bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 disabled:bg-transparent disabled:pl-1 disabled:text-gray-600 disabled:opacity-100 disabled:cursor-default",
    iconSize: 14,
    searchButtonLeft: "left-1",
    searchButtonExtra: "disabled:hidden",
  },
  form: {
    wrapper: "w-full",
    inner: "relative rounded-md",
    input: "w-full h-9 pl-7 pr-7 text-xs border border-gray-300 rounded-md bg-white text-gray-800 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed",
    iconSize: 13,
    searchButtonLeft: "left-2",
    searchButtonExtra: "",
  },
} as const;

const SpreadsheetItemInput = forwardRef<HTMLInputElement, SpreadsheetItemInputProps>(
  ({ value, displayValue, onChange, displayBy, enableValidation = false, onKeyDown, onSelect, onNotFound, onDoubleClick, enableRef = false, disabled = false, size = "table", accountingTypeCodePrefix, accountingTypeGroup, hasIngredient, statusMode }, ref) => {
    const styles = SIZE_STYLES[size];
    const { getItem, searchItems, isPreloading } = useItemCache();
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState("");
    const [isWaitingForCache, setIsWaitingForCache] = useState(false);
    const [validationState, setValidationState] = useState<'none' | 'valid' | 'partial' | 'invalid' | 'multiple'>('none');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const isTypingRef = useRef(false);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [enableRef]);

    // Update search term when value changes (but not while typing)
    useEffect(() => {
      if (isTypingRef.current) return;
      if (!value) {
        setSearchTerm("");
        setValidationState('none');
        return;
      }
      // If displayValue is provided, use it directly (fast path - no API calls)
      if (displayValue !== undefined) {
        setSearchTerm(displayValue);
        return;
      }
      const item = getItem(value);
      if (item) {
        setSearchTerm(displayBy === 'code' ? (item.ident || "") : (item.name || ""));
        if (enableValidation) setValidationState('valid');
      } else if (!isPreloading) {
        setIsWaitingForCache(true);
        const timeoutId = setTimeout(() => {
          const cachedItem = getItem(value);
          if (cachedItem) {
            if (!isTypingRef.current) {
              setSearchTerm(displayBy === 'code' ? (cachedItem.ident || "") : (cachedItem.name || ""));
              if (enableValidation) setValidationState('valid');
            }
            setIsWaitingForCache(false);
            return;
          }

          const apiUrl = process.env.REACT_APP_API_URL ?? '';
          const token = localStorage.getItem('token');
          fetch(`${apiUrl}/api/items/${value}`, { headers: { 'x-auth-token': token ?? '' } })
            .then(res => res.json())
            .then(data => {
              if (!isTypingRef.current) {
                if (data && data._id) {
                  setSearchTerm(displayBy === 'code' ? (data.ident || "") : (data.name || ""));
                  if (enableValidation) setValidationState('valid');
                } else {
                  setSearchTerm("");
                  setValidationState('none');
                }
              }
            })
            .catch(() => {
              if (!isTypingRef.current) {
                setSearchTerm("");
                setValidationState('none');
              }
            })
            .finally(() => setIsWaitingForCache(false));
        }, 300);

        return () => {
          clearTimeout(timeoutId);
          setIsWaitingForCache(false);
        };
      }
    }, [value, displayValue, getItem, displayBy, isPreloading, enableValidation]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      isTypingRef.current = true;
      setSearchTerm(val);
      setValidationState('none');

      if (val === "") {
        onChange("");
        return;
      }
    };

    const validateCode = async (code: string) => {
      if (!code.trim()) {
        setValidationState('none');
        return false;
      }

      try {
        const result = await searchItems(code, 1, { accountingTypeCodePrefix, accountingTypeGroup, hasIngredient, statusMode });
        const exactMatches = result.items.filter(item =>
          item.ident?.toLowerCase() === code.toLowerCase()
        );

        if (exactMatches.length === 1) {
          // Exact match - auto-select
          onChange(exactMatches[0]._id);
          setSearchTerm(displayBy === 'code' ? (exactMatches[0].ident || "") : (exactMatches[0].name || ""));
          setValidationState('valid');
          setTimeout(() => setValidationState('none'), 1500);
          return true;
        } else if (exactMatches.length > 1) {
          // Multiple exact matches - show indicator
          setValidationState('multiple');
          return false;
        } else {
          // Check for partial matches
          const partialMatches = result.items.filter(item =>
            item.ident?.toLowerCase().startsWith(code.toLowerCase())
          );

          if (partialMatches.length === 1) {
            // Single partial match - auto-select
            onChange(partialMatches[0]._id);
            setSearchTerm(displayBy === 'code' ? (partialMatches[0].ident || "") : (partialMatches[0].name || ""));
            setValidationState('valid');
            setTimeout(() => setValidationState('none'), 1500);
            return true;
          } else if (partialMatches.length > 1) {
            // Multiple partial matches - show indicator
            setValidationState('multiple');
            return false;
          } else {
            // No matches - show error
            setValidationState('invalid');
            if (onNotFound) {
              onNotFound(code);
              setTimeout(() => setValidationState('none'), 1000);
              return false;
            }
            setTimeout(() => {
              setSearchTerm("");
              onChange("");
              setValidationState('none');
            }, 1000);
            return false;
          }
        }
      } catch (error) {
        setValidationState('invalid');
        setTimeout(() => {
          setSearchTerm("");
          onChange("");
          setValidationState('none');
        }, 1000);
        return false;
      }
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      // F4 key: open search modal
      if (e.key === 'F4') {
        e.preventDefault();
        setIsModalOpen(true);
        return;
      }

      // Handle Enter key with validation (if enabled)
      if (e.key === "Enter" && enableValidation) {
        if (searchTerm.trim()) {
          // Enter with typed code - instant validation
          e.preventDefault();
          e.stopPropagation();

          // Validate and select item instantly
          await validateCode(searchTerm);

          // Let Enter propagate to parent for normal navigation
          if (onKeyDown) {
            setTimeout(() => onKeyDown(e), 10);
          }
          return;
        }
        // If no search term, let Enter propagate
        if (onKeyDown) {
          onKeyDown(e);
        }
        return;
      }

      // For non-validation mode or other keys, just pass through
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    const getBorderClass = () => {
      if (!enableValidation) return '';
      if (validationState === 'invalid') return 'ring-2 ring-red-500/50';
      if (validationState === 'valid') return 'ring-2 ring-green-500/50';
      if (validationState === 'multiple') return 'ring-2 ring-yellow-500/50';
      return '';
    };

    return (
      <div className={styles.wrapper}>
        <div className={`${styles.inner} ${getBorderClass()}`}>
          {/* Search icon (left side) - opens modal */}
          <button
            className={`absolute ${styles.searchButtonLeft} ${styles.searchButtonExtra} top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors z-10`}
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            title="Search (F4)"
            disabled={disabled}
          >
            <Search size={styles.iconSize} />
          </button>

          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onBlur={() => {
              isTypingRef.current = false;
              if (enableValidation && searchTerm && !value) {
                validateCode(searchTerm);
              }
            }}
            onDoubleClick={onDoubleClick}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={styles.input}
            autoComplete="off"
            placeholder=""
          />

          {/* Validation indicators (only when validation enabled) */}
          {enableValidation && validationState === 'valid' && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-green-500">
              <Check size={14} />
            </div>
          )}
          {enableValidation && validationState === 'multiple' && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 text-yellow-500" title={t("validation.multipleMatches") || "F4 for matches"}>
              <AlertCircle size={14} />
            </div>
          )}

          {/* Loading spinner */}
          {(isWaitingForCache || isPreloading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
              <SmallLoadingSpinner size="xs" />
            </div>
          )}
        </div>

        {/* Search Modal */}
        <ItemSearchModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelect={(item) => {
            onChange(item._id);
            setSearchTerm(displayBy === 'code' ? (item.ident || "") : (item.name || ""));
            if (enableValidation) {
              setValidationState('valid');
              setTimeout(() => setValidationState('none'), 1500);
            }
            // Call onSelect callback to allow parent to move focus to next field
            if (onSelect) {
              setTimeout(() => onSelect(), 0);
            }
          }}
          currentValue={value}
          accountingTypeCodePrefix={accountingTypeCodePrefix}
          accountingTypeGroup={accountingTypeGroup}
          hasIngredient={hasIngredient}
          statusMode={statusMode}
        />
      </div>
    );
  }
);

export default SpreadsheetItemInput;
